import { getter, process, State } from '@progress/kendo-data-query';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
  getSelectedState,
  GridDataStateChangeEvent,
  GridPagerSettings,
  GridRowDoubleClickEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useInvoiceLineItemColumns from 'src/assets/static/enter_invoice/invoiceLineItemColumns';
import Button from 'src/components/design-system/Button';
import GridWrapper from 'src/components/GridWrapper';
import useInvoiceLineItemsApi from 'src/services/useInvoiceLineItemsApi';
import InvoiceGridToolbar from './InvoiceGridToolBar';

const DATA_ITEM_KEY: string = 'receiptLineItemId';
const LineItemIdGetter = getter(DATA_ITEM_KEY);

const initialDataState: State = {
  skip: 0,
  take: 20,
  sort: [],
};
const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'single',
};
const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};
const defaultProps = {
  tableTextId: 'Line Items',
  DataItemKey: DATA_ITEM_KEY,
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

interface IProps {
  title: string;
  showModal: () => void;
  invoiceId: string;
  saveLineItem?: () => void;
  isOcr?: boolean;
}

const InvoiceLineItem: React.FC<IProps> = ({
  title,
  invoiceId,
  showModal,
  saveLineItem,
  isOcr,
}) => {
  const [dataState, setDataState] = useState(initialDataState);
  const [selectedRow, setSelectedRow] = useState<{
    [id: string]: boolean | number[];
  }>({});
  const { getInvoiceLineItems, isInvoiceLineItemsLoading, invoiceLineItems, setInvoiceLineItems } =
    useInvoiceLineItemsApi(invoiceId);
  const invoiceLineItemColumns = useInvoiceLineItemColumns();

  const { data: processData, total } = useMemo(
    () => process(invoiceLineItems || [], dataState),
    [invoiceLineItems, dataState]
  );

  useEffect(() => {
    getInvoiceLineItems();
  }, [getInvoiceLineItems]);

  const onDataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };
  const onSelectionChange = React.useCallback(
    (event: GridSelectionChangeEvent) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedRow,
        dataItemKey: DATA_ITEM_KEY,
      });
      setSelectedRow(newSelectedState);
      setInvoiceLineItems((prev: any) => {
        const selectedPOList = prev.map((item: any) => ({
          ...item,
          selected: newSelectedState[LineItemIdGetter(item)],
        }));
        return selectedPOList;
      });
    },
    [selectedRow, setInvoiceLineItems]
  );

  const onRowDoubleClick = (event: GridRowDoubleClickEvent) => {
    const key = event.dataItem[DATA_ITEM_KEY];
    setInvoiceLineItems((prev) => {
      const lineItems = [...prev].map((item) => {
        if (item.receiptLineItemId === key) {
          return { ...item, selected: false };
        } else return item;
      });
      return lineItems;
    });
  };

  const handleClearFilter = useCallback(() => {
    setDataState((prev) => ({
      ...initialDataState,
      skip: prev.skip,
      take: prev.take,
    }));
  }, []);

  const isClearFiltersDisabled = useMemo(
    () => !dataState.filter?.filters.length,
    [dataState.filter?.filters.length]
  );

  return (
    <>
      <p className="font-bold text-sm mt-2 mb-2">{title}</p>
      <GridWrapper
        id="invoiceLineItemGrid"
        columns={invoiceLineItemColumns}
        dataSource={processData}
        dataState={dataState}
        height={'75vh'}
        total={total}
        onDataStateChange={onDataStateChange}
        onSelectionChange={!isOcr ? onSelectionChange : undefined}
        {...defaultProps}
        loading={isInvoiceLineItemsLoading}
        gridToolbarChildren={
          <InvoiceGridToolbar
            clearFilter={handleClearFilter}
            isClearFiltersDisabled={isClearFiltersDisabled}
          />
        }
        onRowDoubleClick={!isOcr ? onRowDoubleClick : undefined}
      />
      <DialogActionsBar>
        <div className="flex justify-end gap-2">
          {!isOcr && (
            <Button onClick={saveLineItem} color="info">
              Exit and Select Record
            </Button>
          )}
          <Button color="info" onClick={showModal}>
            {isOcr ? 'Close' : 'Exit'}
          </Button>
        </div>
      </DialogActionsBar>
    </>
  );
};
export default InvoiceLineItem;
